import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react';
import { ConfigProvider, theme, type ThemeConfig } from 'antd';
import type { Locale } from 'antd/es/locale';
import enUS from 'antd/locale/en_US';
import { lightTheme, darkTheme } from '../theme/themeConfig';
import { useLanguageOptional } from './I18nContext';
import { SUPPORTED_LANGUAGES, type LanguageCode } from '../i18n/languages';

// AntD's own component strings (DatePicker, pagination, Select empty text, etc.) localize
// alongside app strings via ConfigProvider's `locale`. Only English is bundled eagerly
// (it is the default and fallback); the other locale bundles are code-split and loaded on
// demand when the language changes, so they stay out of the initial download. Until a
// non-English bundle resolves, AntD's component strings briefly show English while app
// strings switch immediately. If no I18nProvider is an ancestor, AntD stays on English
// (the app simply isn't localized); the provider works standalone in that case.
// Partial on purpose: a language in the registry without an AntD locale here simply keeps
// AntD's English component strings (the app's own strings still translate). Adding a
// language to one app never forces an entry to exist for it here.
const antdLocaleLoaders: Partial<Record<Exclude<LanguageCode, 'en'>, () => Promise<{ default: Locale }>>> = {
  es: () => import('antd/locale/es_ES'),
  fr: () => import('antd/locale/fr_FR'),
  de: () => import('antd/locale/de_DE'),
  ja: () => import('antd/locale/ja_JP'),
  pt: () => import('antd/locale/pt_PT'),
  'pt-BR': () => import('antd/locale/pt_BR'),
  ar: () => import('antd/locale/ar_EG'),
  cs: () => import('antd/locale/cs_CZ'),
  da: () => import('antd/locale/da_DK'),
  it: () => import('antd/locale/it_IT'),
  ko: () => import('antd/locale/ko_KR'),
  nl: () => import('antd/locale/nl_NL'),
  pl: () => import('antd/locale/pl_PL'),
  sv: () => import('antd/locale/sv_SE'),
};

type ThemeMode = 'light' | 'dark';
// `null` means the user hasn't picked a theme yet, so we follow the OS preference
// live. Any non-null value is an explicit override the user set via toggleTheme.
type ThemeOverride = ThemeMode | null;

const STORAGE_KEY = 'theme';

const ThemeContext = createContext<{ themeMode: ThemeMode; toggleTheme: () => void } | undefined>(undefined);

export const useTheme = () => {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme must be used within ThemeProvider');
  return ctx;
};

const getSystemTheme = (): ThemeMode => {
  if (typeof window === 'undefined' || !window.matchMedia) return 'light';
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
};

const getStoredOverride = (): ThemeOverride => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored === 'light' || stored === 'dark' ? stored : null;
  } catch {
    // localStorage can throw in private mode or with strict cookie settings.
    return null;
  }
};

interface ThemeProviderProps {
  children: ReactNode;
  /** An app with its own look passes its token sets here; others get the shared ones. */
  themes?: { light: ThemeConfig; dark: ThemeConfig };
}

export const ThemeProvider = ({ children, themes }: ThemeProviderProps) => {
  const light = themes?.light ?? lightTheme;
  const dark = themes?.dark ?? darkTheme;
  // Optional: when no I18nProvider is present, fall back to the default language so the
  // theme can be used standalone. AntD then shows its English component strings; app
  // strings are unaffected (this provider does not own them).
  const language = useLanguageOptional()?.language ?? 'en';
  // Writing direction for the active language. Drives AntD's component mirroring via
  // ConfigProvider; the document-level <html dir> is handled in I18nContext. Falls back
  // to 'ltr' if the language is somehow unknown.
  const direction = SUPPORTED_LANGUAGES.find((l) => l.code === language)?.dir ?? 'ltr';
  const [loadedLocale, setLoadedLocale] = useState<{ language: LanguageCode; locale: Locale } | null>(null);
  const antdLocale = loadedLocale?.language === language ? loadedLocale.locale : enUS;
  const [override, setOverride] = useState<ThemeOverride>(getStoredOverride);
  const [systemTheme, setSystemTheme] = useState<ThemeMode>(getSystemTheme);
  const themeMode: ThemeMode = override ?? systemTheme;

  // Load the active AntD locale bundle on demand (English is already eager). A language
  // with no AntD loader keeps English component strings rather than failing.
  useEffect(() => {
    const loader = language === 'en' ? undefined : antdLocaleLoaders[language];
    if (!loader) return;
    let active = true;
    void loader().then((m) => {
      if (active) setLoadedLocale({ language, locale: m.default });
    });
    return () => {
      active = false;
    };
  }, [language]);

  // Track the OS preference. The state only affects the rendered theme when
  // override is null, but we keep it current either way so toggling back to
  // auto (future feature) would be instantaneous.
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = (e: MediaQueryListEvent) => {
      setSystemTheme(e.matches ? 'dark' : 'light');
    };
    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  const toggleTheme = useCallback(() => {
    setOverride((prev) => {
      const current = prev ?? systemTheme;
      const next: ThemeMode = current === 'light' ? 'dark' : 'light';
      try {
        localStorage.setItem(STORAGE_KEY, next);
      } catch {
        // Storage failure is non-fatal; the override still applies for the session.
      }
      return next;
    });
  }, [systemTheme]);

  useEffect(() => {
    const themeTokens = themeMode === 'light' ? light.token : dark.token;
    const appBg = themeTokens?.colorBgLayout ?? themeTokens?.colorBgBase ?? '#ffffff';
    const root = document.documentElement;
    root.style.setProperty('--app-bg', appBg);
    document.body.style.backgroundColor = appBg;

    if (themeMode === 'dark') {
      root.style.setProperty('--scrollbar-track', '#2a2a2a');
      root.style.setProperty('--scrollbar-thumb', '#555');
      root.style.setProperty('--scrollbar-thumb-hover', '#777');
    } else {
      root.style.setProperty('--scrollbar-track', '#f1f1f1');
      root.style.setProperty('--scrollbar-thumb', '#c1c1c1');
      root.style.setProperty('--scrollbar-thumb-hover', '#a8a8a8');
    }
  }, [themeMode, light, dark]);

  // Inject global scrollbar styles once
  useEffect(() => {
    const id = 'sf-scrollbar-styles';
    if (document.getElementById(id)) return;
    const style = document.createElement('style');
    style.id = id;
    style.textContent = `
      * { scrollbar-width: thin; scrollbar-color: var(--scrollbar-thumb) var(--scrollbar-track); }
      html { scrollbar-gutter: stable; }
      /* The stable gutter already holds the scrollbar's space, so Ant Design's scroll lock must not
         also shrink the body by that width while a Drawer or Modal is open. */
      html body { width: auto !important; }
      ::-webkit-scrollbar { width: 8px; height: 8px; }
      ::-webkit-scrollbar-track { background: var(--scrollbar-track); }
      ::-webkit-scrollbar-thumb { background: var(--scrollbar-thumb); border-radius: 4px; }
      ::-webkit-scrollbar-thumb:hover { background: var(--scrollbar-thumb-hover); }
      .ant-layout-sider[style*="position: fixed"] { transition: none !important; }
    `;
    document.head.appendChild(style);
  }, []);

  return (
    <ThemeContext.Provider value={{ themeMode, toggleTheme }}>
      <ConfigProvider direction={direction} locale={antdLocale} theme={{ ...(themeMode === 'light' ? light : dark), algorithm: themeMode === 'dark' ? theme.darkAlgorithm : theme.defaultAlgorithm }}>
        {children}
      </ConfigProvider>
    </ThemeContext.Provider>
  );
};
